import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '@/api';
import Card from '../components/Card';
import { HiArrowLeft } from 'react-icons/hi2';
import toast from 'react-hot-toast';

const InputField = ({ label, error, ...props }) => (
    <div>
        <label htmlFor={props.name} className="block text-sm font-medium text-pro-dark-text-secondary">{label}</label>
        <input 
            {...props}
            className={`mt-1 w-full p-2 bg-pro-dark-surface border rounded-md focus:ring-pro-dark-accent focus:border-pro-dark-accent ${error ? 'border-red-500' : 'border-pro-dark-border'}`}
        />
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
);

const ModifierCandidatPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({}); // État pour les erreurs
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [preview, setPreview] = useState(null);
    const [isPreviewing, setIsPreviewing] = useState(false);

    useEffect(() => {
        const fetchCandidat = async () => {
            setIsLoading(true);
            try {
                const response = await api.get(`/candidats/${id}`);
                setFormData(response.data);
            } catch (error) {
                console.error("Erreur lors de la récupération du candidat", error);
                navigate('/candidats');
            } finally {
                setIsLoading(false);
            }
        };
        fetchCandidat();
    }, [id, navigate]);

    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'number' ? parseInt(value, 10) || 0 : value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.prenom) newErrors.prenom = "Le prénom est requis.";
        if (!formData.nom) newErrors.nom = "Le nom est requis.";
        if (!formData.numeroBullhorn) newErrors.numeroBullhorn = "Le N° Bullhorn est requis.";
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            toast.error("Veuillez remplir tous les champs obligatoires.");
            return;
        }

        setIsSaving(true);
        try {
            await api.put(`/candidats/${id}`, formData);
            toast.success('Candidat mis à jour avec succès !');
            navigate('/candidats');
        } catch (error) {
            console.error("Erreur lors de la mise à jour:", error);
            toast.error(`Erreur: ${error.response?.data?.detail || 'Une erreur est survenue.'}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handlePreview = async () => {
        setIsPreviewing(true);
        try {
            const response = await api.post('/candidats/summary', { text: JSON.stringify(formData) });
            setPreview(response.data);
        } catch (error) {
            toast.error("Erreur lors de la génération du résumé");
        } finally {
            setIsPreviewing(false);
        }
    };
    
    const fields = [
      { label: 'Prénom', name: 'prenom' },
      { label: 'Nom', name: 'nom' },
      { label: 'N° Bullhorn', name: 'numeroBullhorn' },
      { label: 'Sourcer', name: 'sourcer' },
      { label: 'Profil', name: 'profil' },
      { label: 'EP/EM', name: 'epem' },
      { label: 'Mobilité', name: 'mobilite' },
      { label: 'Prise de Réf', name: 'priseDeRef' },
      { label: 'Format Pixie', name: 'formatPixie' },
      { label: 'XP', name: 'xp', type: 'number' },
      { label: 'Dispo', name: 'dispo', type: 'date' },
      { label: 'Salaire actuel', name: 'salaireActuel' },
      { label: 'Statut', name: 'statut' },
      { label: 'PS', name: 'ps' },
      { label: 'Situation process', name: 'situationProcess' },
      { label: 'Titre de séjour', name: 'titreDeSejour' },
      { label: 'Anglais', name: 'anglais' },
      { label: 'Poste Actuel', name: 'posteActuel' },
      { label: 'Piste Pixie', name: 'pistePixie' }
    ];

    if (isLoading) {
        return <p className="text-center text-pro-dark-text-secondary">Chargement...</p>;
    }

    return (
        <div>
            <Link to="/candidats" className="flex items-center gap-2 text-sm text-pro-dark-text-secondary hover:text-pro-dark-accent mb-6">
                <HiArrowLeft />
                Retour à la liste des candidats
            </Link>
            <h1 className="text-3xl font-bold text-pro-dark-text-primary mb-8">Modifier le candidat</h1>
            <Card>
                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {fields.map(field => (
                        <InputField 
                            key={field.name} 
                            {...field}
                            value={formData[field.name] || ''}
                            onChange={handleInputChange}
                            error={errors[field.name]}
                        />
                    ))}
                    <div className="md:col-span-3">
                        <label htmlFor="competences" className="block text-sm font-medium text-pro-dark-text-secondary">Compétences</label>
                        <textarea id="competences" name="competences" rows="4" value={formData.competences || ''} onChange={handleInputChange} className="mt-1 w-full p-2 bg-pro-dark-surface border border-pro-dark-border rounded-md focus:ring-pro-dark-accent focus:border-pro-dark-accent"></textarea>
                    </div>
                    <div className="md:col-span-3 text-right">
                        <div className="flex justify-end gap-4">
                            <button
                                type="button"
                                onClick={handlePreview}
                                disabled={isPreviewing}
                                className="px-6 py-2 bg-pro-dark-surface border border-pro-dark-border rounded-lg text-pro-dark-text-secondary hover:text-pro-dark-accent"
                            >
                                {isPreviewing ? 'Génération...' : 'Prévisualiser'}
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className="px-6 py-2 bg-pro-dark-accent text-white rounded-lg hover:bg-pro-dark-accent-hover disabled:bg-gray-600"
                            >
                                {isSaving ? 'Mise à jour...' : 'Mettre à jour'}
                            </button>
                        </div>
                    </div>
                    {preview && (
                        <div className="md:col-span-3 p-4 bg-pro-dark-surface border border-pro-dark-border rounded-lg">
                            <h3 className="text-lg font-semibold mb-2">Résumé</h3>
                            <p className="text-sm whitespace-pre-wrap mb-2">{preview.summary}</p>
                            <button
                                type="button"
                                onClick={() => navigator.clipboard.writeText(preview.summary)}
                                className="text-sm text-pro-dark-accent mb-4"
                            >
                                Copier le résumé
                            </button>
                            <h3 className="text-lg font-semibold mb-2">Email</h3>
                            <p className="text-sm whitespace-pre-wrap mb-2">{preview.email_template}</p>
                            <button
                                type="button"
                                onClick={() => navigator.clipboard.writeText(preview.email_template)}
                                className="text-sm text-pro-dark-accent"
                            >
                                Copier l'e-mail
                            </button>
                        </div>
                    )}
                </form>
            </Card>
        </div>
    );
};

export default ModifierCandidatPage;